import React, { useState, useContext } from "react";
import { Form, Formik, useField } from "formik";
import * as Yup from "yup";
import { Link, Redirect } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleNotch,
  faEnvelope,
  faLock,
  faArrowRight,
  faCheckCircle,
  faExclamationCircle,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { AuthContext } from "../context/AuthContext";
import { FetchContext } from "../context/FetchContext";
import logo from "../images/logo.svg";

const LoginSchema = Yup.object().shape({
  email: Yup.string().required("Email is required"),
  password: Yup.string().required("Password is required"),
});

const IconField = ({ name, type, placeholder, ariaLabel, icon, trailing }) => {
  const [field, meta] = useField(name);
  const hasError = meta.touched && meta.error;
  return (
    <div>
      <div
        className={`flex items-center rounded-xl border bg-white transition shadow-sm focus-within:ring-2 focus-within:ring-primary focus-within:border-primary ${
          hasError ? "border-red-400" : "border-gray-300"
        }`}
      >
        <span className="pl-3 pr-2 text-gray-400">
          <FontAwesomeIcon icon={icon} />
        </span>
        <input
          {...field}
          aria-label={ariaLabel}
          type={type}
          placeholder={placeholder}
          className="flex-1 bg-transparent py-3 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
        />
        {trailing && <span className="pr-2">{trailing}</span>}
      </div>
      {hasError && (
        <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
          <FontAwesomeIcon icon={faExclamationCircle} />
          <span>{meta.error}</span>
        </p>
      )}
    </div>
  );
};

const BrandPanel = () => (
  <div className="relative hidden lg:flex lg:w-1/2 overflow-hidden">
    <div aria-hidden className="absolute inset-0 bg-gradient opacity-70" />
    <div aria-hidden className="hero-blob hero-blob--teal" />
    <div aria-hidden className="hero-blob hero-blob--peach" />

    <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
      <Link to="/" className="inline-flex items-center gap-3">
        <img src={logo} alt="Relation" className="h-8" />
      </Link>

      <div className="max-w-md">
        <span className="inline-flex items-center gap-2 rounded-full bg-white bg-opacity-80 border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 mb-6">
          <span className="w-2 h-2 rounded-full bg-primary" />
          Welcome back
        </span>
        <h1 className="text-3xl xl:text-4xl font-extrabold tracking-tight text-gray-900 leading-tight">
          Ship your product in{" "}
          <span className="text-primary">every language</span>.
        </h1>
        <p className="mt-4 text-gray-700 leading-relaxed">
          Sign in to pick up where you left off — manage keys, review
          translations, and keep releases moving.
        </p>

        <ul className="mt-8 space-y-3">
          {[
            "Collaborate with translators and reviewers",
            "Version releases alongside your app",
            "AI drafts via DeepL, polished by humans",
          ].map((line) => (
            <li key={line} className="flex items-start gap-3 text-gray-800">
              <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs">
                <FontAwesomeIcon icon={faCheckCircle} />
              </span>
              <span className="text-sm">{line}</span>
            </li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-gray-600">
        © {new Date().getFullYear()} Relation. All rights reserved.
      </p>
    </div>
  </div>
);

const Login = () => {
  const authContext = useContext(AuthContext);
  const fetchContext = useContext(FetchContext);
  const [loginSuccess, setLoginSuccess] = useState();
  const [loginError, setLoginError] = useState();
  const [redirectOnLogin, setRedirectOnLogin] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submitCredentials = async (credentials) => {
    try {
      setLoginLoading(true);
      const { data } = await fetchContext.publicAxios.post(
        `/login`,
        credentials
      );

      authContext.setAuthState(data);
      setLoginSuccess(data.message);
      setLoginError(null);

      setTimeout(() => {
        setRedirectOnLogin(true);
      }, 700);
    } catch (error) {
      setLoginLoading(false);
      console.log(error);
      const { data } = error.response;
      setLoginError(data.message);
      setLoginSuccess(null);
    }
  };

  return (
    <>
      {redirectOnLogin && <Redirect to="/projects" />}
      <div className="min-h-screen w-full flex bg-white">
        <BrandPanel />

        <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-10 lg:w-1/2 relative">
          <div
            aria-hidden
            className="lg:hidden absolute inset-0 bg-gradient opacity-40"
          />
          <div className="relative w-full max-w-md">
            <div className="lg:hidden flex justify-center mb-8">
              <img src={logo} alt="Relation" className="h-10" />
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10">
              <div className="mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  Log in to your account
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  New to Relation?{" "}
                  <Link
                    to="/signup"
                    className="font-semibold text-primary hover:opacity-80 transition"
                  >
                    Create an account
                  </Link>
                </p>
              </div>

              <Formik
                initialValues={{ email: "", password: "" }}
                onSubmit={(values) => submitCredentials(values)}
                validationSchema={LoginSchema}
              >
                {() => (
                  <Form className="space-y-4">
                    {loginSuccess && (
                      <div className="flex items-start gap-2 rounded-xl border border-green-200 bg-green-50 p-3">
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="mt-0.5 text-green-600"
                        />
                        <p className="text-sm text-green-800">{loginSuccess}</p>
                      </div>
                    )}
                    {loginError && (
                      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
                        <FontAwesomeIcon
                          icon={faExclamationCircle}
                          className="mt-0.5 text-red-600"
                        />
                        <p className="text-sm text-red-700">{loginError}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Email
                      </label>
                      <IconField
                        ariaLabel="Email"
                        name="email"
                        type="text"
                        placeholder="you@company.com"
                        icon={faEnvelope}
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-sm font-semibold text-gray-700">
                          Password
                        </label>
                        <Link
                          to="/forgot-password"
                          className="text-xs font-semibold text-primary hover:opacity-80 transition"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <IconField
                        ariaLabel="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        icon={faLock}
                        trailing={
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                          >
                            <FontAwesomeIcon
                              icon={showPassword ? faEyeSlash : faEye}
                            />
                          </button>
                        }
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loginLoading}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-white text-sm font-semibold px-6 py-3 shadow-lg hover:shadow-xl hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {loginLoading ? (
                        <>
                          <FontAwesomeIcon icon={faCircleNotch} spin />
                          <span>Signing in...</span>
                        </>
                      ) : (
                        <>
                          <span>Log in</span>
                          <FontAwesomeIcon icon={faArrowRight} />
                        </>
                      )}
                    </button>
                  </Form>
                )}
              </Formik>
            </div>

            <p className="mt-6 text-center text-xs text-gray-500 lg:hidden">
              © {new Date().getFullYear()} Relation. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
